import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import { schemaLabel } from '@viewer/lib/ifcSchema'
import { useIdsValidation } from './useIdsValidation'
import { IdsStaleNotice, IdsSummaryBar, IdsSpecList } from './IdsResults'
import IdsAuditoria from './IdsAuditoria'
import { downloadBlob } from '@core/lib/downloadBlob'
import { escreverXlsx } from '@core/lib/folhas'
import { modeloDeRequisitos } from './lib/idsDeFolha'

// Painel flutuante "Validar IDS": atalho rápido a partir do ribbon (separador
// Comparação) para quem está no viewer. Abre um ficheiro .ids, valida o modelo
// escolhido contra as especificações e mostra um relatório navegável, sem sair
// do 3D. A lógica de validação e a apresentação do relatório são partilhadas
// com a página do módulo (useIdsValidation + IdsResults); aqui fica só a moldura
// flutuante e o canal de pré-preenchimento por deep link.

export default function IdsPanel() {
  const open = useAppStore((s) => s.idsPanelOpen)
  const setOpen = useAppStore((s) => s.setIdsPanelOpen)
  const idsPrefill = useAppStore((s) => s.idsPrefill)
  const setIdsPrefill = useAppStore((s) => s.setIdsPrefill)

  const v = useIdsValidation({ active: open })
  const {
    models,
    modelId,
    setModelSel,
    ids,
    idsName,
    report,
    reportModelId,
    busy,
    step,
    error,
    show3d,
    setShow3d,
    bcfMsg,
    bcfEnabled,
    expanded,
    toggleSpec,
    totals,
    stale,
    dismissStale,
    loadIdsFile,
    adoptIds,
    validate,
    exportCsv,
    exportBcf,
  } = v

  const fileRef = useRef<HTMLInputElement | null>(null)

  // Pré-preenchimento vindo da ligação partilhável (?ids=https://...): o
  // DeepLinkLoader descarrega o .ids e empurra {xml,name} pelo store.
  // Adotamos DURANTE o render (padrão recomendado do React para "ajustar
  // estado quando um valor externo muda"), com guarda por referência para
  // consumir cada pedido uma vez. Espelha o filterPrefill do FloatingFilterPanel.
  const [lastPrefill, setLastPrefill] = useState<{
    xml: string
    name: string
  } | null>(null)
  if (idsPrefill && idsPrefill !== lastPrefill) {
    setLastPrefill(idsPrefill)
    adoptIds(idsPrefill.xml, idsPrefill.name)
  }
  // Limpar o canal do store depois de consumido (setter do store, não é estado
  // local) para não reaplicar o mesmo IDS num reabrir manual.
  useEffect(() => {
    if (idsPrefill) setIdsPrefill(null)
  }, [idsPrefill, setIdsPrefill])

  if (!open) return null

  const onPickFile = () => fileRef.current?.click()

  return (
    <div className="pointer-events-auto absolute right-3 top-14 z-20 flex max-h-[80%] w-[420px] flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-sm shadow-2xl">
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <span className="font-medium">Validar IDS</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="ml-auto rounded text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          ✕
        </button>
      </div>

      {stale && <IdsStaleNotice onDismiss={dismissStale} />}

      {models.length === 0 ? (
        <div className="px-3 py-3 text-xs text-[var(--color-text-muted)]">
          Carrega um modelo IFC primeiro para o validar contra um IDS.
        </div>
      ) : (
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".ids,.xlsx,.csv,application/xml,text/xml"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = '' // permitir re-abrir o mesmo ficheiro
              if (file) void loadIdsFile(file)
            }}
            className="hidden"
          />
          <div className="flex flex-col gap-2 border-b border-[var(--color-border)] px-3 py-2 text-xs">
            {!ids && (
              <p className="leading-snug text-[var(--color-text-muted)]">
                Um IDS (buildingSMART Information Delivery Specification)
                descreve requisitos de informação: que elementos devem existir e
                que atributos/propriedades têm de ter. Abre um ficheiro .ids para
                validar este modelo.
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onPickFile}
                className="rounded border border-[var(--color-border)] px-2 py-1 hover:text-[var(--color-text)]"
              >
                Abrir .ids ou folha Excel
              </button>
              <button
                type="button"
                onClick={() => downloadBlob(escreverXlsx(modeloDeRequisitos()), 'requisitos_modelo.xlsx')}
                title="Folha de Excel para escrever requisitos de informação e abrir aqui como IDS"
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                Modelo Excel
              </button>
              {idsName && (
                <span
                  className="truncate text-[var(--color-text-muted)]"
                  title={idsName}
                >
                  {idsName}
                </span>
              )}
            </div>
            {ids && (
              <>
                <div className="text-[var(--color-text-dim)]">
                  {ids.info.title ? `"${ids.info.title}" · ` : ''}
                  {ids.specs.length} especificaç
                  {ids.specs.length === 1 ? 'ão' : 'ões'}
                </div>
                <label className="flex flex-col gap-0.5">
                  <span className="text-[var(--color-text-dim)]">
                    Modelo a validar
                  </span>
                  <select
                    value={modelId}
                    onChange={(e) => setModelSel(e.target.value)}
                    className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1"
                  >
                    {models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.fileName} · {schemaLabel(m.schemaVersion)}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => void validate()}
                  disabled={busy || !modelId}
                  className="self-start rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-50"
                >
                  {busy ? 'A validar…' : 'Validar'}
                </button>
                {modelId && (
                  <IdsAuditoria
                    ids={ids}
                    modelId={modelId}
                    esquema={models.find((m) => m.id === modelId)?.schemaVersion ?? null}
                  />
                )}
              </>
            )}
            {step && <p className="text-[var(--color-text-dim)]">{step}</p>}
            {error && <p className="text-[var(--color-error-text)]">{error}</p>}
          </div>

          {report && totals && (
            <>
              <IdsSummaryBar
                totals={totals}
                show3d={show3d}
                setShow3d={setShow3d}
                onExportCsv={exportCsv}
                onExportBcf={exportBcf}
                bcfEnabled={bcfEnabled}
              />
              {bcfMsg && (
                <div className="shrink-0 border-b border-[var(--color-border)] px-3 py-1 text-[11px] text-[var(--color-ok-text)]">
                  {bcfMsg}
                </div>
              )}
              <IdsSpecList
                report={report}
                modelId={reportModelId}
                expanded={expanded}
                toggleSpec={toggleSpec}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
